import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useToast } from '@/hooks/useToast';
import DarkPageContainer from '@/shared/components/DarkPageContainer';
import { captureError } from '@/utils/errorHandler';
import { useApps } from '@/hooks/queries';

export default function AppSummaryPage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { showToast, toasts, hideToast } = useToast();
  const { data: appsData } = useApps();
  const hasApps = appsData && appsData.length > 0;

  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      showToast('Please describe what you need', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact/pro-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: user?.name || user?.email || 'Unknown',
          email: user?.email || '',
          description: description.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send inquiry');
      }

      setIsSubmitted(true);
    } catch (error) {
      captureError(error, {
        action: 'handleSubmit',
        component: 'AppSummaryPage',
      });
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="2xl">
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-black text-white mb-4">We'll be in touch.</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
            Thanks — we've received your brief and will get back to you within 24 hours to discuss.
          </p>
          {hasApps ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="text-zapt-yellow hover:text-zapt-yellow/80 font-semibold underline underline-offset-2 transition-colors"
            >
              Go to dashboard
            </button>
          ) : (
            <Link
              to="/"
              className="text-zapt-yellow hover:text-zapt-yellow/80 font-semibold underline underline-offset-2 transition-colors"
            >
              Back to home
            </Link>
          )}
        </div>
      </DarkPageContainer>
    );
  }

  return (
    <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="2xl">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-3">
          Tell us what you need
        </h1>
        <p className="text-gray-400 text-lg">
          Describe what you're trying to solve or build. We'll get back to you within 24 hours to discuss.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            What do you need? <span className="text-red-400">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            required
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-zapt-purple focus:border-transparent transition-all resize-none text-base"
            placeholder="e.g. We manage deliveries manually with spreadsheets and WhatsApp. I need something to plan routes, assign drivers and capture proof of delivery..."
            autoFocus
          />
          <p className="text-gray-500 text-sm mt-2">No need to be precise — a rough description is fine.</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !description.trim()}
          className="w-full py-4 bg-zapt-yellow text-zapt-navy rounded-xl font-bold text-lg hover:bg-zapt-yellow/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Sending...
            </span>
          ) : (
            'Send →'
          )}
        </button>

        <div className="text-center mt-6">
          {hasApps ? (
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-300 text-sm underline transition-colors"
            >
              Back to dashboard
            </button>
          ) : (
            <Link
              to="/"
              className="text-gray-500 hover:text-gray-300 text-sm underline transition-colors"
            >
              Back to home
            </Link>
          )}
        </div>
      </form>
    </DarkPageContainer>
  );
}
